import React, { useState, useRef, useEffect } from 'react';
import { useQuery } from 'react-query';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  Package, 
  ShoppingCart, 
  Wallet, 
  Percent,
  Activity,
  Users,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  CreditCard,
  X,
  Bitcoin,
  Plus,
  PieChart as PieChartIcon,
  HelpCircle,
  Gamepad2
} from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Label, PieChart, Pie, Cell, Legend, ReferenceLine } from 'recharts';
import { format } from 'date-fns';

// Color palette for pie charts
const COLORS = ['#3b198e', '#5a2bb8', '#7a3de2', '#9a5ff0', '#ba81ff', '#da9dff', '#f0b9ff', '#ffd5ff'];

// Custom Line Chart Tooltip Component - SIMPLIFIED
const CustomLineTooltip = ({ active, payload, coordinate, viewBox, chartType, statsData, trendDays }) => {
  const currentData = chartType === 'orders' ? statsData?.ordersTrend : statsData?.balanceChanges;
  
  if (!active || !payload || !payload.length || !coordinate || !currentData?.length || !viewBox) {
    return null;
  }
  
  // Parse date correctly based on format from backend
  let formattedDate;
  if (trendDays === 1) {
    const date = new Date(payload[0].payload.date + 'Z');
    formattedDate = format(date, 'MMM dd, h:mm a');
  } else {
    const [year, month, day] = payload[0].payload.date.split('-');
    formattedDate = `${parseInt(month)}/${parseInt(day)}/${year}`;
  }
  
  const dataValue = payload[0].value;
  
  // USE RECHARTS' COORDINATE.Y - IT ALREADY KNOWS WHERE THE POINT IS!
  const dataPointY = coordinate.y;
  const chartHeight = viewBox.height || 300;
  
  // Position tooltip above if point is in lower half, below if in upper half
  const tooltipAbove = dataPointY > (viewBox.y + chartHeight / 2);
  
  // SIMPLIFIED: Fixed height tooltip (50px) with simple positioning
  // Arrow is 5px outside box, we want 20px gap from arrow tip to point
  // Downward arrow (above point): tooltipTop = dataPointY - 20 - 5 - 50 = dataPointY - 75
  // Upward arrow (below point): tooltipTop = dataPointY + 20 + 5 = dataPointY + 25
  const tooltipTop = tooltipAbove ? dataPointY - 75 : dataPointY + 25;
  
  return (
    <div style={{
      position: 'absolute',
      left: `${coordinate.x}px`,
      top: `${tooltipTop}px`,
      transform: 'translateX(-50%)',
      backgroundColor: 'var(--primary-bg)',
      border: '1px solid var(--border-color)',
      borderRadius: '6px',
      padding: '8px 12px',
      minHeight: '50px',
      height: '50px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
      fontSize: '12px',
      fontWeight: '500',
      color: '#f3f4f6',
      whiteSpace: 'nowrap',
      animation: 'bob 2s ease-in-out infinite',
      transformOrigin: tooltipAbove ? 'center bottom' : 'center top',
      pointerEvents: 'none',
      transition: 'left 0.1s ease-out, top 0.1s ease-out'
    }}>
      {tooltipAbove ? (
        <>
          <div style={{
            position: 'absolute',
            bottom: '-5px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid var(--primary-bg)'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid var(--border-color)',
            zIndex: -1
          }} />
        </>
      ) : (
        <>
          <div style={{
            position: 'absolute',
            top: '-5px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: '5px solid var(--primary-bg)'
          }} />
          <div style={{
            position: 'absolute',
            top: '-6px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderBottom: '6px solid var(--border-color)',
            zIndex: -1
          }} />
        </>
      )}
      <div style={{ marginBottom: '1px', fontSize: '11px', color: '#9ca3af' }}>
        {formattedDate}
      </div>
      <div style={{ fontWeight: '600', color: '#f3f4f6' }}>
        ${dataValue.toFixed(2)}
      </div>
    </div>
  );
};

// Custom Tooltip Component
const CustomPieTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    
    return (
      <div
        style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid #4a4a4a',
          borderRadius: '8px',
          padding: '12px 16px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
        }}
      >
        <p style={{ color: 'white', margin: 0, fontWeight: '600', fontSize: '14px' }}>
          {data.name}
        </p>
        <p style={{ color: 'var(--theme-color)', margin: '4px 0 0 0', fontSize: '16px', fontWeight: '700' }}>
          {data.payload.formatted || data.value}
        </p>
      </div>
    );
  }
  return null;
};

const Dashboard = () => {
  const { user, token } = useAuth();
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupPaymentMethod, setTopupPaymentMethod] = useState(null);
  const [trendDays, setTrendDays] = useState(7);
  const [daysDropdownOpen, setDaysDropdownOpen] = useState(false);
  const [chartType, setChartType] = useState('orders'); // 'orders' or 'balance'
  const [chartTypeDropdownOpen, setChartTypeDropdownOpen] = useState(false);
  const [universalDays, setUniversalDays] = useState(7);
  const [universalDaysDropdownOpen, setUniversalDaysDropdownOpen] = useState(false);
  const [showUniversalTooltip, setShowUniversalTooltip] = useState(false);
  
  // Individual duration states for each pie chart
  const [revenueByProductDays, setRevenueByProductDays] = useState(7);
  const [ordersByProductDays, setOrdersByProductDays] = useState(7);
  const [revenueByCategoryDays, setRevenueByCategoryDays] = useState(7);
  const [ordersByCategoryDays, setOrdersByCategoryDays] = useState(7);
  
  // Animation keys to force re-animation on every duration change
  const [revenueByProductAnimKey, setRevenueByProductAnimKey] = useState(0);
  const [ordersByProductAnimKey, setOrdersByProductAnimKey] = useState(0);
  const [revenueByCategoryAnimKey, setRevenueByCategoryAnimKey] = useState(0);
  const [ordersByCategoryAnimKey, setOrdersByCategoryAnimKey] = useState(0);
  
  // Dropdown states for individual selectors
  const [revenueByProductDropdownOpen, setRevenueByProductDropdownOpen] = useState(false);
  const [ordersByProductDropdownOpen, setOrdersByProductDropdownOpen] = useState(false);
  const [revenueByCategoryDropdownOpen, setRevenueByCategoryDropdownOpen] = useState(false);
  const [ordersByCategoryDropdownOpen, setOrdersByCategoryDropdownOpen] = useState(false);
  // console.log('User object:', user); // Debug log - uncomment to debug user data
  
  const { data: stats, isLoading: statsLoading } = useQuery(
    ['dashboard-stats', trendDays],
    () => {
      // Get user's timezone offset in minutes (e.g., -420 for UTC-7)
      const timezoneOffset = new Date().getTimezoneOffset();
      return dashboardAPI.getStats({ days: trendDays, timezoneOffset });
    },
    {
      enabled: !!token,
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  );

  const { data: balance, isLoading: balanceLoading } = useQuery(
    'dashboard-balance',
    dashboardAPI.getBalance,
    {
      enabled: !!token,
    }
  );

  const { data: activity, isLoading: activityLoading } = useQuery(
    'dashboard-activity',
    () => dashboardAPI.getActivity({ limit: 10 }),
    {
      enabled: !!token,
    }
  );

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (daysDropdownOpen && !event.target.closest('.days-dropdown-container')) {
        setDaysDropdownOpen(false);
      }
      if (chartTypeDropdownOpen && !event.target.closest('.chart-type-dropdown-container')) {
        setChartTypeDropdownOpen(false);
      }
      if (universalDaysDropdownOpen && !event.target.closest('.universal-days-dropdown-container')) {
        setUniversalDaysDropdownOpen(false);
      }
      if (revenueByProductDropdownOpen && !event.target.closest('.revenue-by-product-dropdown')) {
        setRevenueByProductDropdownOpen(false);
      }
      if (ordersByProductDropdownOpen && !event.target.closest('.orders-by-product-dropdown')) {
        setOrdersByProductDropdownOpen(false);
      }
      if (revenueByCategoryDropdownOpen && !event.target.closest('.revenue-by-category-dropdown')) {
        setRevenueByCategoryDropdownOpen(false);
      }
      if (ordersByCategoryDropdownOpen && !event.target.closest('.orders-by-category-dropdown')) {
        setOrdersByCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [daysDropdownOpen, chartTypeDropdownOpen, universalDaysDropdownOpen, revenueByProductDropdownOpen, ordersByProductDropdownOpen, revenueByCategoryDropdownOpen, ordersByCategoryDropdownOpen]);

  const daysOptions = [
    { value: 1, label: 'Last 24 Hours' },
    { value: 7, label: 'Last 7 Days' },
    { value: 14, label: 'Last 14 Days' },
    { value: 30, label: 'Last 30 Days' }
  ];

  const chartTypeOptions = [
    { value: 'orders', label: 'Product Orders' },
    { value: 'balance', label: 'Balance Changes' }
  ];


  const { data: health } = useQuery(
    'dashboard-health',
    dashboardAPI.getHealth,
    {
      enabled: !!token,
      refetchInterval: 60000, // Refresh every minute
    }
  );

  // Pie chart data queries
  const { data: revenueByProduct } = useQuery(
    ['revenue-by-product', revenueByProductDays],
    () => dashboardAPI.getRevenueByProduct({ days: revenueByProductDays }),
    {
      enabled: !!token,
      refetchInterval: 30000,
    }
  );

  const { data: ordersByProduct } = useQuery(
    ['orders-by-product', ordersByProductDays],
    () => dashboardAPI.getOrdersByProduct({ days: ordersByProductDays }),
    {
      enabled: !!token,
      refetchInterval: 30000,
    }
  );

  const { data: revenueByCategory } = useQuery(
    ['revenue-by-category', revenueByCategoryDays],
    () => dashboardAPI.getRevenueByCategory({ days: revenueByCategoryDays }),
    {
      enabled: !!token,
      refetchInterval: 30000,
    }
  );

  const { data: ordersByCategory } = useQuery(
    ['orders-by-category', ordersByCategoryDays],
    () => dashboardAPI.getOrdersByCategory({ days: ordersByCategoryDays }),
    {
      enabled: !!token,
      refetchInterval: 30000,
    }
  );

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const statsData = stats?.data?.data;
  const balanceData = balance?.data?.data?.balance;
  const activityData = activity?.data?.data?.activities || [];
  const healthData = health?.data?.data?.health;
  
  // Pie chart data with formatted values for tooltip
  const revenueByProductData = (revenueByProduct?.data?.data || []).map(item => ({
    ...item,
    formatted: `$${item.value.toFixed(2)}`
  }));
  const ordersByProductData = (ordersByProduct?.data?.data || []).map(item => ({
    ...item,
    formatted: `${item.value} orders`
  }));
  const revenueByCategoryData = (revenueByCategory?.data?.data || []).map(item => ({
    ...item,
    formatted: `$${item.value.toFixed(2)}`
  }));
  const ordersByCategoryData = (ordersByCategory?.data?.data || []).map(item => ({
    ...item,
    formatted: `${item.value} orders`
  }));

  // Status badge component
  const StatusBadge = ({ status, children }) => {
    const statusClasses = {
      completed: 'badge-success',
      pending: 'badge-warning',
      failed: 'badge-danger',
      processing: 'badge-info'
    };

    return (
      <span className={`badge ${statusClasses[status] || 'badge-secondary'}`}>
        {children}
      </span>
    );
  };

  // Stat card component
  const StatCard = ({ title, value, icon: Icon, color = 'primary', showButton = false }) => {
    return (
      <div className="stat-card-container">
        <div className="card stat-card" style={{padding: '16px', position: 'relative'}}>
          <div className="flex items-center w-full">
            <div className="flex-shrink-0">
              <div className="stat-icon-bg p-3 rounded-lg" style={{backgroundColor: '#0a0a0a'}}>
                <Icon className="stat-icon h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-4 flex-1">
              <p className="text-sm font-medium text-gray-400">{title}</p>
              <p className="text-2xl font-semibold text-gray-100">{value}</p>
            </div>
          </div>
          {showButton && (
            <div style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              backgroundColor: '#3b198e',
              borderRadius: '6px',
              padding: '8px 12px',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#2d1270';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#3b198e';
              e.currentTarget.style.transform = 'scale(1)';
            }}
            onClick={() => setShowTopupModal(true)}
            >
              <div className="flex items-center gap-1 text-sm font-medium" style={{ color: '#ffffff' }}>
                <Plus className="h-4 w-4" />
                Add Funds
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Dual stat card component for Revenue/Net Profit
  const DualStatCard = ({ title1, value1, title2, value2, icon: Icon, color = 'primary' }) => {
    return (
      <div className="stat-card-container">
        <div className="card stat-card" style={{padding: '16px'}}>
          <div className="flex items-center w-full">
            <div className="flex-shrink-0">
              <div className="stat-icon-bg p-3 rounded-lg" style={{backgroundColor: '#0a0a0a'}}>
                <Icon className="stat-icon h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-4 flex-1 grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-400">{title1}</p>
                <p className="text-2xl font-semibold text-gray-100">{value1}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-400">{title2}</p>
                <p className="text-2xl font-semibold text-gray-100">{value2}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Stats Cards - Moved to top with larger spacing */}
      <div className="mb-7 mt-7">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Discount"
          value={`${user?.discount || user?.discount_percentage || 0}%`}
          icon={Percent}
        />
        <StatCard
          title="Account Balance"
          value={`$${(balanceData?.total || 0).toFixed(2)}`}
          icon={CreditCard}
          showButton={true}
        />
        {user?.role === 'admin' ? (
          <DualStatCard
            title1="Revenue"
            value1={`$${(statsData?.totalRevenue || 0).toFixed(2)}`}
            title2="Net Profit"
            value2={`$${(statsData?.netProfit || 0).toFixed(2)}`}
            icon={Wallet}
          />
        ) : (
          <StatCard
            title="Revenue"
            value={`$${(statsData?.totalRevenue || 0).toFixed(2)}`}
            icon={Wallet}
          />
        )}
        <StatCard
          title="Total Products"
          value={statsData?.totalProducts || 0}
          icon={Package}
        />
        </div>
      </div>

      {/* System Health Alert */}
      {healthData && healthData.status !== 'healthy' && (
        <div className="rounded-md bg-yellow-50 p-4 mb-6">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                System Health Warning
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>Some system components may not be functioning properly.</p>
              </div>
            </div>
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
        {/* Trend Chart */}
        {statsData?.ordersTrend && statsData.ordersTrend.length > 0 && (
          <div className="card dashboard-card">
            <div className="card-header">
              <div className="flex items-center justify-start" style={{ gap: '0.5rem' }}>
                <div className="days-dropdown-container" style={{ position: 'relative', minWidth: '150px' }}>
                  <div
                    onClick={() => setDaysDropdownOpen(!daysDropdownOpen)}
                    style={{ 
                      width: '100%',
                      backgroundColor: '#0a0a0a', 
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      paddingTop: '0.5rem',
                      paddingBottom: '0.5rem',
                      paddingLeft: '1rem',
                      paddingRight: '2.5rem',
                      cursor: 'pointer',
                      position: 'relative',
                      color: '#ffffff',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                  >
                    {daysOptions.find(opt => opt.value === trendDays)?.label}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#ffffff" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: daysDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                        width: '1.25rem',
                        height: '1.25rem',
                        transition: 'transform 0.2s ease',
                        pointerEvents: 'none'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  
                  {daysDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: '#0a0a0a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        animation: 'dropdownSlide 0.2s ease-out'
                      }}
                    >
                      {daysOptions.map(option => (
                        <div
                          key={option.value}
                          onClick={() => {
                            setTrendDays(option.value);
                            setDaysDropdownOpen(false);
                          }}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            transition: 'background-color 0.15s ease',
                            backgroundColor: option.value === trendDays ? '#3a3a3a' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === trendDays ? '#3a3a3a' : 'transparent'}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="chart-type-dropdown-container" style={{ position: 'relative', minWidth: '180px' }}>
                  <div
                    onClick={() => setChartTypeDropdownOpen(!chartTypeDropdownOpen)}
                    style={{ 
                      width: '100%',
                      backgroundColor: '#0a0a0a', 
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      paddingTop: '0.5rem',
                      paddingBottom: '0.5rem',
                      paddingLeft: '1rem',
                      paddingRight: '2.5rem',
                      cursor: 'pointer',
                      position: 'relative',
                      color: '#ffffff',
                      fontSize: '0.875rem',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                  >
                    {chartTypeOptions.find(opt => opt.value === chartType)?.label}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#ffffff" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: chartTypeDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                        width: '1.25rem',
                        height: '1.25rem',
                        transition: 'transform 0.2s ease',
                        pointerEvents: 'none'
                      }}
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </div>
                  
                  {chartTypeDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        backgroundColor: '#0a0a0a',
                        border: '1px solid #4a4a4a',
                        borderRadius: '8px',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1000,
                        animation: 'dropdownSlide 0.2s ease-out'
                      }}
                    >
                      {chartTypeOptions.map(option => (
                        <div
                          key={option.value}
                          onClick={() => {
                            setChartType(option.value);
                            setChartTypeDropdownOpen(false);
                          }}
                          style={{
                            padding: '0.75rem 1rem',
                            cursor: 'pointer',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            transition: 'background-color 0.15s ease',
                            backgroundColor: option.value === chartType ? '#3a3a3a' : 'transparent'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === chartType ? '#3a3a3a' : 'transparent'}
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="card-content" style={{ overflow: 'visible', position: 'relative' }}>
              <style>{`
                @keyframes bob {
                  0%, 100% { transform: translate(-50%, 0px); }
                  50% { transform: translate(-50%, -8px); }
                }
                .recharts-cartesian-axis-line {
                  stroke-width: 1 !important;
                }
                .recharts-wrapper .recharts-surface {
                  overflow: visible;
                }
                /* Hide top border */
                .recharts-cartesian-grid-horizontal line:last-child {
                  opacity: 0;
                }
                /* Force all vertical grid lines to be 1px */
                .recharts-cartesian-grid-vertical line {
                  stroke-width: 1 !important;
                }
                /* Apply opacity gradient to vertical lines except first one */
                .recharts-cartesian-grid-vertical line:not(:first-child) {
                  stroke: url(#verticalLineGradient) !important;
                }
              `}</style>
              {(() => {
                // Calculate symmetric domain for Balance Changes chart to center $0 vertically
                const chartData = chartType === 'orders' ? (statsData?.ordersTrend || []) : (statsData?.balanceChanges || []);
                let yAxisDomain = ['auto', 'auto'];
                
                if (chartType === 'balance' && chartData.length > 0) {
                  const dataKey = 'change';
                  const values = chartData.map(d => d[dataKey] || 0);
                  const maxAbs = Math.max(...values.map(v => Math.abs(v)), 1); // At least 1 to avoid 0 domain
                  yAxisDomain = [-maxAbs, maxAbs];
                }
                
                return (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart 
                      data={chartData} 
                      key={`${trendDays}-${chartType}`}
                      margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                    >
                      <defs>
                    <linearGradient id="verticalLineGradient" x1="0%" y1="0%" x2="0%" y2="100%" gradientUnits="userSpaceOnUse">
                      <stop offset="0%" stopColor="#525252" stopOpacity="0" />
                      <stop offset="25%" stopColor="#525252" stopOpacity="1" />
                      <stop offset="100%" stopColor="#525252" stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid 
                    stroke="#525252" 
                    strokeWidth={1} 
                    strokeDasharray="0"
                  />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(value) => {
                      if (trendDays === 1) {
                        // For 24 hours, parse as UTC and convert to local timezone
                        const date = new Date(value + 'Z');
                        return format(date, 'h a');
                      } else {
                        // For other durations, format date string directly without timezone conversion
                        const [year, month, day] = value.split('-');
                        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                        return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}`;
                      }
                    }}
                    interval={trendDays === 1 ? 1 : trendDays === 30 ? 1 : trendDays === 14 ? 1 : 0}
                    ticks={trendDays === 1 ? (() => {
                      const data = chartType === 'orders' ? (statsData?.ordersTrend || []) : (statsData?.balanceChanges || []);
                      return data.filter((_, index) => index % 2 === 0).map(d => d.date);
                    })() : undefined}
                    angle={-39}
                    textAnchor="end"
                    height={60}
                    tick={{ fill: '#9ca3af' }}
                    axisLine={{ stroke: '#5a5a5a' }}
                    tickLine={{ stroke: '#5a5a5a' }}
                  />
                  <YAxis 
                    tickFormatter={(value) => `$${value}`}
                    tick={{ fill: '#9ca3af' }}
                    axisLine={{ stroke: '#5a5a5a' }}
                    tickLine={{ stroke: '#5a5a5a' }}
                    domain={yAxisDomain}
                  />
                  {chartType === 'balance' && (
                    <ReferenceLine y={0} stroke="#6b7280" strokeWidth={2} strokeDasharray="3 3" />
                  )}
                  <Tooltip 
                    cursor={{ stroke: 'var(--theme-color)', strokeWidth: 1, strokeDasharray: '5 5' }}
                    allowEscapeViewBox={{ x: true, y: true }}
                    wrapperStyle={{ zIndex: 1000 }}
                    content={(props) => (
                      <CustomLineTooltip 
                        {...props} 
                        chartType={chartType}
                        statsData={statsData}
                        trendDays={trendDays}
                      />
                    )}
                  />
                  <Line 
                    type="monotone" 
                    dataKey={chartType === 'orders' ? 'revenue' : 'change'}
                    stroke="var(--theme-color)" 
                    strokeWidth={2}
                    name={chartType === 'orders' ? 'Revenue' : 'Balance Change'}
                  />
                </LineChart>
              </ResponsiveContainer>
                );
              })()}
            </div>
          </div>
        )}

        {/* Top Products */}
        {statsData?.topProducts && statsData.topProducts.length > 0 && (
          <div className="card dashboard-card">
            <div className="card-header">
              <h3 className="card-title">Top Products</h3>
            </div>
            <div className="card-content">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statsData.topProducts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="orderCount" fill="#3b82f6" name="Orders" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Recent Activity */}
        <div className="card dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Recent Activity</h3>
        </div>
        <div className="card-content variants-scrollbar" style={{ overflowY: 'auto', maxHeight: '368px' }}>
          {activityLoading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : activityData.length > 0 ? (
            <div className="flow-root">
              <ul className="-mb-8">
                {activityData.map((item, index) => (
                  <li key={item.id}>
                    <div className="relative pb-8">
                      {index !== activityData.length - 1 && (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5"
                          style={{ backgroundColor: '#2a2a2a' }}
                          aria-hidden="true"
                        />
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center" style={{ boxShadow: '0 0 0 8px var(--primary-bg)' }}>
                            {item.status === 'completed' && <CheckCircle className="h-4 w-4 text-white" />}
                            {item.status === 'pending' && <Clock className="h-4 w-4 text-white" />}
                            {item.status === 'failed' && <XCircle className="h-4 w-4 text-white" />}
                            {!['completed', 'pending', 'failed'].includes(item.status) && (
                              <Activity className="h-4 w-4 text-white" />
                            )}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                          <div>
                            <p className="text-sm text-gray-100">{item.title}</p>
                            <p className="text-sm text-gray-400">{item.description}</p>
                            {item.amount && (
                              <p className="text-sm font-medium text-green-600">
                                ${item.amount.toFixed(2)}
                              </p>
                            )}
                          </div>
                          <div className="text-right text-sm whitespace-nowrap text-gray-400">
                            <div>{format(new Date(item.timestamp + 'Z'), 'MMM dd, h:mm a')}</div>
                            <StatusBadge status={item.status}>
                              {item.status}
                            </StatusBadge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="text-center py-6">
              <Activity className="mx-auto h-12 w-12 text-gray-500" />
              <h3 className="mt-2 text-sm font-medium text-gray-100">No recent activity</h3>
              <p className="mt-1 text-sm text-gray-400">
                Activity will appear here as orders are processed.
              </p>
            </div>
          )}
        </div>
        </div>
      </div>

      {/* Universal Duration Selector */}
      <div className="card dashboard-card mb-6" style={{ overflow: 'visible' }}>
        <div className="card-content" style={{ overflow: 'visible' }}>
          <div className="flex items-center" style={{ gap: '1rem' }}>
            <div className="universal-days-dropdown-container" style={{ position: 'relative', minWidth: '200px' }}>
              <div
                onClick={() => setUniversalDaysDropdownOpen(!universalDaysDropdownOpen)}
                style={{ 
                  width: '100%',
                  backgroundColor: '#0a0a0a', 
                  border: '1px solid #4a4a4a',
                  borderRadius: '8px',
                  paddingTop: '0.6rem',
                  paddingBottom: '0.6rem',
                  paddingLeft: '1rem',
                  paddingRight: '2rem',
                  cursor: 'pointer',
                  position: 'relative',
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
              >
                <PieChartIcon size={16} style={{ flexShrink: 0, color: 'var(--theme-color)' }} />
                <span>{daysOptions.find(opt => opt.value === universalDays)?.label}</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="#ffffff" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  style={{
                    position: 'absolute',
                    right: '0.5rem',
                    top: '50%',
                    transform: universalDaysDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                    width: '1.25rem',
                    height: '1.25rem',
                    transition: 'transform 0.2s ease',
                    pointerEvents: 'none'
                  }}
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
              
              {universalDaysDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    left: 0,
                    right: 0,
                    backgroundColor: '#0a0a0a',
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1000,
                    animation: 'dropdownSlide 0.2s ease-out'
                  }}
                >
                  {daysOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setUniversalDays(option.value);
                        // Update all individual chart durations
                        setRevenueByProductDays(option.value);
                        setOrdersByProductDays(option.value);
                        setRevenueByCategoryDays(option.value);
                        setOrdersByCategoryDays(option.value);
                        // Increment animation keys to force re-animation
                        setRevenueByProductAnimKey(prev => prev + 1);
                        setOrdersByProductAnimKey(prev => prev + 1);
                        setRevenueByCategoryAnimKey(prev => prev + 1);
                        setOrdersByCategoryAnimKey(prev => prev + 1);
                        setUniversalDaysDropdownOpen(false);
                      }}
                      style={{
                        padding: '0.75rem 1rem',
                        cursor: 'pointer',
                        color: '#ffffff',
                        fontSize: '0.875rem',
                        transition: 'background-color 0.15s ease',
                        backgroundColor: option.value === universalDays ? '#3a3a3a' : 'transparent'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === universalDays ? '#3a3a3a' : 'transparent'}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div 
              style={{ position: 'relative', display: 'inline-block' }}
              onMouseEnter={() => setShowUniversalTooltip(true)}
              onMouseLeave={() => setShowUniversalTooltip(false)}
            >
              <HelpCircle 
                size={18} 
                style={{ 
                  color: '#9ca3af', 
                  cursor: 'help',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#d1d5db'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
              />
              {showUniversalTooltip && (
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: 'calc(100% + 8px)',
                    transform: 'translateX(-50%)',
                    backgroundColor: 'var(--primary-bg)',
                    border: '1px solid var(--border-color)',
                    color: '#ffffff',
                    padding: '0.75rem 1rem',
                    borderRadius: '8px',
                    fontSize: '0.875rem',
                    whiteSpace: 'normal',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                    zIndex: 1001,
                    minWidth: '450px',
                    lineHeight: '1.5'
                  }}
                >
                  <div style={{ marginBottom: '0.25rem', fontWeight: '600' }}>Universal Duration Selector</div>
                  <div style={{ fontSize: '0.8125rem', color: '#d1d5db' }}>
                    Changing this selector will update all pie charts below to the same duration. Individual chart durations can be customized and will persist until this universal selector is changed again.
                  </div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '6px solid transparent',
                      borderRight: '6px solid transparent',
                      borderTop: '6px solid var(--primary-bg)'
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Pie Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 mb-6">
        {/* Revenue by Product */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Package size={28} style={{ color: 'var(--theme-color)', position: 'relative', top: '2px' }} />
                Revenue Amount By Product
              </div>
              <div className="revenue-by-product-dropdown" style={{ position: 'relative', minWidth: '150px', top: '2px' }}>
                <div
                  onClick={() => setRevenueByProductDropdownOpen(!revenueByProductDropdownOpen)}
                  style={{ 
                    width: '100%',
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    paddingTop: '0.6rem',
                    paddingBottom: '0.6rem',
                    paddingLeft: '1rem',
                    paddingRight: '2rem',
                    cursor: 'pointer',
                    position: 'relative',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    transition: 'border-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                >
                  {daysOptions.find(opt => opt.value === revenueByProductDays)?.label}
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#ffffff" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      right: '0.5rem',
                      top: '50%',
                      transform: revenueByProductDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                      width: '1.25rem',
                      height: '1.25rem',
                      transition: 'transform 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                
                {revenueByProductDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'dropdownSlide 0.2s ease-out'
                    }}
                  >
                    {daysOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setRevenueByProductDays(option.value);
                          setRevenueByProductAnimKey(prev => prev + 1);
                          setRevenueByProductDropdownOpen(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: option.value === revenueByProductDays ? '#3a3a3a' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === revenueByProductDays ? '#3a3a3a' : 'transparent'}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </h3>
          </div>
          <div className="card-content" style={{ paddingLeft: '1.5rem', paddingTop: '0.5rem', paddingRight: '0.5rem', paddingBottom: '0.5rem' }}>
            {revenueByProductData.length > 0 ? (
              <ResponsiveContainer key={`revenue-product-${revenueByProductAnimKey}`} width="100%" height={310}>
                <PieChart>
                  <Pie
                    data={revenueByProductData}
                    cx={134}
                    cy={155}
                    labelLine={false}
                    label={false}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="none"
                    isAnimationActive={true}
                    animationBegin={0}
                    animationDuration={1200}
                    animationEasing="ease-in-out"
                  >
                    {revenueByProductData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="top"
                    iconType="square"
                    wrapperStyle={{ paddingRight: '250px', backgroundColor: 'var(--secondary-bg)' }}
                    formatter={(value) => <span style={{ color: 'white', marginLeft: '8px' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[310px] text-gray-400">
                No revenue data available for selected period
              </div>
            )}
          </div>
        </div>

        {/* Orders by Product */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Package size={28} style={{ color: 'var(--theme-color)', position: 'relative', top: '2px' }} />
                Order Amount By Product
              </div>
              <div className="orders-by-product-dropdown" style={{ position: 'relative', minWidth: '150px', top: '2px' }}>
                <div
                  onClick={() => setOrdersByProductDropdownOpen(!ordersByProductDropdownOpen)}
                  style={{ 
                    width: '100%',
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    paddingTop: '0.6rem',
                    paddingBottom: '0.6rem',
                    paddingLeft: '1rem',
                    paddingRight: '2rem',
                    cursor: 'pointer',
                    position: 'relative',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    transition: 'border-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                >
                  {daysOptions.find(opt => opt.value === ordersByProductDays)?.label}
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#ffffff" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: ordersByProductDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                      width: '1.25rem',
                      height: '1.25rem',
                      transition: 'transform 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                
                {ordersByProductDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'dropdownSlide 0.2s ease-out'
                    }}
                  >
                    {daysOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setOrdersByProductDays(option.value);
                          setOrdersByProductAnimKey(prev => prev + 1);
                          setOrdersByProductDropdownOpen(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: option.value === ordersByProductDays ? '#3a3a3a' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === ordersByProductDays ? '#3a3a3a' : 'transparent'}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </h3>
          </div>
          <div className="card-content" style={{ paddingLeft: '1.5rem', paddingTop: '0.5rem', paddingRight: '0.5rem', paddingBottom: '0.5rem' }}>
            {ordersByProductData.length > 0 ? (
              <ResponsiveContainer key={`orders-product-${ordersByProductAnimKey}`} width="100%" height={310}>
                <PieChart>
                  <Pie
                    data={ordersByProductData}
                    cx={134}
                    cy={155}
                    labelLine={false}
                    label={false}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="none"
                    isAnimationActive={true}
                    animationBegin={0}
                    animationDuration={1200}
                    animationEasing="ease-in-out"
                  >
                    {ordersByProductData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="top"
                    iconType="square"
                    wrapperStyle={{ paddingRight: '250px', backgroundColor: 'var(--secondary-bg)' }}
                    formatter={(value) => <span style={{ color: 'white', marginLeft: '8px' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[310px] text-gray-400">
                No order data available for selected period
              </div>
            )}
          </div>
        </div>

        {/* Revenue by Category */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Gamepad2 size={28} style={{ color: 'var(--theme-color)', position: 'relative', top: '2px' }} />
                Revenue Amount By Category
              </div>
              <div className="revenue-by-category-dropdown" style={{ position: 'relative', minWidth: '150px', top: '2px' }}>
                <div
                  onClick={() => setRevenueByCategoryDropdownOpen(!revenueByCategoryDropdownOpen)}
                  style={{ 
                    width: '100%',
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    paddingTop: '0.6rem',
                    paddingBottom: '0.6rem',
                    paddingLeft: '1rem',
                    paddingRight: '2rem',
                    cursor: 'pointer',
                    position: 'relative',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    transition: 'border-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                >
                  {daysOptions.find(opt => opt.value === revenueByCategoryDays)?.label}
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#ffffff" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: revenueByCategoryDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                      width: '1.25rem',
                      height: '1.25rem',
                      transition: 'transform 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                
                {revenueByCategoryDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'dropdownSlide 0.2s ease-out'
                    }}
                  >
                    {daysOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setRevenueByCategoryDays(option.value);
                          setRevenueByCategoryAnimKey(prev => prev + 1);
                          setRevenueByCategoryDropdownOpen(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: option.value === revenueByCategoryDays ? '#3a3a3a' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === revenueByCategoryDays ? '#3a3a3a' : 'transparent'}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </h3>
          </div>
          <div className="card-content" style={{ paddingLeft: '1.5rem', paddingTop: '0.5rem', paddingRight: '0.5rem', paddingBottom: '0.5rem' }}>
            {revenueByCategoryData.length > 0 ? (
              <ResponsiveContainer key={`revenue-category-${revenueByCategoryAnimKey}`} width="100%" height={310}>
                <PieChart>
                  <Pie
                    data={revenueByCategoryData}
                    cx={134}
                    cy={155}
                    labelLine={false}
                    label={false}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="none"
                    isAnimationActive={true}
                    animationBegin={0}
                    animationDuration={1200}
                    animationEasing="ease-in-out"
                  >
                    {revenueByCategoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="top"
                    iconType="square"
                    wrapperStyle={{ paddingRight: '250px', backgroundColor: 'var(--secondary-bg)' }}
                    formatter={(value) => <span style={{ color: 'white', marginLeft: '8px' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[310px] text-gray-400">
                No revenue data available for selected period
              </div>
            )}
          </div>
        </div>

        {/* Orders by Category */}
        <div className="card dashboard-card">
          <div className="card-header">
            <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Gamepad2 size={28} style={{ color: 'var(--theme-color)', position: 'relative', top: '2px' }} />
                Order Amount By Category
              </div>
              <div className="orders-by-category-dropdown" style={{ position: 'relative', minWidth: '150px', top: '2px' }}>
                <div
                  onClick={() => setOrdersByCategoryDropdownOpen(!ordersByCategoryDropdownOpen)}
                  style={{ 
                    width: '100%',
                    backgroundColor: '#0a0a0a', 
                    border: '1px solid #4a4a4a',
                    borderRadius: '8px',
                    paddingTop: '0.6rem',
                    paddingBottom: '0.6rem',
                    paddingLeft: '1rem',
                    paddingRight: '2rem',
                    cursor: 'pointer',
                    position: 'relative',
                    color: '#ffffff',
                    fontSize: '0.875rem',
                    transition: 'border-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d1d1'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = '#4a4a4a'}
                >
                  {daysOptions.find(opt => opt.value === ordersByCategoryDays)?.label}
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="#ffffff" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: ordersByCategoryDropdownOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                      width: '1.25rem',
                      height: '1.25rem',
                      transition: 'transform 0.2s ease',
                      pointerEvents: 'none'
                    }}
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </div>
                
                {ordersByCategoryDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      backgroundColor: '#0a0a0a',
                      border: '1px solid #4a4a4a',
                      borderRadius: '8px',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      animation: 'dropdownSlide 0.2s ease-out'
                    }}
                  >
                    {daysOptions.map(option => (
                      <div
                        key={option.value}
                        onClick={() => {
                          setOrdersByCategoryDays(option.value);
                          setOrdersByCategoryAnimKey(prev => prev + 1);
                          setOrdersByCategoryDropdownOpen(false);
                        }}
                        style={{
                          padding: '0.75rem 1rem',
                          cursor: 'pointer',
                          color: '#ffffff',
                          fontSize: '0.875rem',
                          transition: 'background-color 0.15s ease',
                          backgroundColor: option.value === ordersByCategoryDays ? '#3a3a3a' : 'transparent'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a3a3a'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = option.value === ordersByCategoryDays ? '#3a3a3a' : 'transparent'}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </h3>
          </div>
          <div className="card-content" style={{ paddingLeft: '1.5rem', paddingTop: '0.5rem', paddingRight: '0.5rem', paddingBottom: '0.5rem' }}>
            {ordersByCategoryData.length > 0 ? (
              <ResponsiveContainer key={`orders-category-${ordersByCategoryAnimKey}`} width="100%" height={310}>
                <PieChart>
                  <Pie
                    data={ordersByCategoryData}
                    cx={134}
                    cy={155}
                    labelLine={false}
                    label={false}
                    outerRadius={140}
                    fill="#8884d8"
                    dataKey="value"
                    stroke="none"
                    isAnimationActive={true}
                    animationBegin={0}
                    animationDuration={1200}
                    animationEasing="ease-in-out"
                  >
                    {ordersByCategoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend 
                    layout="vertical" 
                    align="right"
                    verticalAlign="top"
                    iconType="square"
                    wrapperStyle={{ paddingRight: '250px', backgroundColor: 'var(--secondary-bg)' }}
                    formatter={(value) => <span style={{ color: 'white', marginLeft: '8px' }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[310px] text-gray-400">
                No order data available for selected period
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Topup Modal */}
      {showTopupModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #4a4a4a',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            position: 'relative'
          }}>
            <button
              onClick={() => {
                setShowTopupModal(false);
                setTopupAmount('');
                setTopupPaymentMethod(null);
              }}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                backgroundColor: 'transparent',
                border: 'none',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '4px'
              }}
            >
              <X className="h-6 w-6" />
            </button>

            <h2 style={{ color: '#ffffff', fontSize: '24px', fontWeight: 'bold', marginBottom: '24px' }}>
              Top Up Balance
            </h2>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                Amount (USD)
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="Enter amount"
                style={{
                  width: '100%',
                  backgroundColor: '#0a0a0a',
                  border: '1px solid #4a4a4a',
                  borderRadius: '6px',
                  padding: '12px',
                  color: '#ffffff',
                  fontSize: '16px',
                  outline: 'none'
                }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ color: '#9ca3af', fontSize: '14px', display: 'block', marginBottom: '12px' }}>
                Select Payment Method
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div
                  onClick={() => setTopupPaymentMethod('btc')}
                  style={{
                    backgroundColor: '#1a1a1a',
                    border: topupPaymentMethod === 'btc' ? '2px solid #3b198e' : '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Bitcoin className="h-8 w-8" style={{ color: '#f7931a' }} />
                  <span style={{ color: '#e5e5e5', fontSize: '16px', fontWeight: '500' }}>Bitcoin</span>
                </div>

                <div
                  onClick={() => setTopupPaymentMethod('ltc')}
                  style={{
                    backgroundColor: '#1a1a1a',
                    border: topupPaymentMethod === 'ltc' ? '2px solid #3b198e' : '1px solid #4a4a4a',
                    borderRadius: '8px',
                    padding: '16px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Bitcoin className="h-8 w-8" style={{ color: '#345d9d' }} />
                  <span style={{ color: '#e5e5e5', fontSize: '16px', fontWeight: '500' }}>Litecoin</span>
                </div>
              </div>
            </div>

            <button
              onClick={async () => {
                if (!topupAmount || parseFloat(topupAmount) <= 0) {
                  toast.error('Please enter a valid amount');
                  return;
                }
                if (!topupPaymentMethod) {
                  toast.error('Please select a payment method');
                  return;
                }

                try {
                  const response = await fetch('/api/btcpay/create-topup-invoice', {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                      amount: parseFloat(topupAmount),
                      paymentMethod: topupPaymentMethod
                    })
                  });

                  const data = await response.json();
                  if (data.success) {
                    window.open(data.data.checkoutLink, '_blank');
                    setShowTopupModal(false);
                    setTopupAmount('');
                    setTopupPaymentMethod(null);
                  } else {
                    toast.error(data.message || 'Failed to create payment');
                  }
                } catch (error) {
                  toast.error('Failed to process payment');
                }
              }}
              style={{
                width: '100%',
                backgroundColor: '#3b198e',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
